import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { notifications } from "@mantine/notifications";
import { mappingApi } from "@services/mappingApi";
import { ApiError } from "@utils/httpClient";
import { MappingStatus } from "@models/job";
import { MappingResultKind, type MappingResultRow } from "@models/mappingResult";
import type { ItemRequestPreviewResponse } from "@models/job";

export interface UseItemRequestResult {
  /** Rows the pipeline found no catalogue entry for — what the mail is about. */
  missingCount: number;
  opened: boolean;
  loading: boolean;
  sending: boolean;
  preview: ItemRequestPreviewResponse | null;
  open: () => Promise<void>;
  close: () => void;
  send: (edited: { subject: string; body: string; to: string[] }) => Promise<void>;
}

/**
 * WD catalogue-entry request flow. A TM `no_match` means a missing value,
 * not a missing item, and must not open this request flow.
 */
export const useItemRequest = (
  jobId: string,
  rows: MappingResultRow[],
): UseItemRequestResult => {
  const { t } = useTranslation();
  const [opened, setOpened] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState<ItemRequestPreviewResponse | null>(null);

  // Counted off `mappingStatus` — the PIPELINE's verdict, so this is "what did
  // the run conclude", which is what the button should offer. The server then
  // narrows to the rows still awaiting an entry (a reviewer who corrected a
  // miss onto a real candidate takes it off the request), so the mail can
  // legitimately cover fewer rows than this count promised.
  const missingCount = rows.filter(
    (row) => row.kind === MappingResultKind.Oe && row.mappingStatus === MappingStatus.NoMatch,
  ).length;

  const fail = useCallback(
    (err: unknown, fallbackKey: string, fallback: string): void => {
      notifications.show({
        color: "red",
        title: t("jobResultsPage.itemRequest.failedTitle", {
          defaultValue: "Item request failed",
        }),
        message: describeError(err, t(fallbackKey, { defaultValue: fallback })),
        withCloseButton: true,
      });
    },
    [t],
  );

  const open = useCallback(async (): Promise<void> => {
    setOpened(true);
    setLoading(true);
    setPreview(null);
    try {
      setPreview(await mappingApi.previewItemRequest(jobId));
    } catch (err) {
      setOpened(false);
      fail(
        err,
        "jobResultsPage.itemRequest.previewFailed",
        "Could not build the item request.",
      );
    } finally {
      setLoading(false);
    }
  }, [jobId, fail]);

  const close = useCallback((): void => {
    setOpened(false);
    setPreview(null);
  }, []);

  const send = useCallback(
    async ({
      subject,
      body,
      to,
    }: {
      subject: string;
      body: string;
      to: string[];
    }) => {
      if (!preview) return;
      setSending(true);
      try {
        const sent = await mappingApi.sendItemRequest(jobId, {
          // Echoed from the preview so the sent mail and its reported count
          // describe exactly what the reviewer looked at.
          row_indexes: preview.row_indexes,
          // Sent explicitly rather than left to the server default: the modal
          // seeds the field FROM that default, so whatever comes back here is
          // the reviewer's decision — including the case where they replaced it.
          to,
          subject,
          body,
        });
        notifications.show({
          color: "teal",
          title: t("jobResultsPage.itemRequest.sentTitle", {
            defaultValue: "Item request sent",
          }),
          message: t("jobResultsPage.itemRequest.sent", {
            defaultValue:
              "Sent {{count}} missing entries to {{to}}, with the spreadsheet attached.",
            count: sent.row_count,
            to: sent.to.join(", "),
          }),
          withCloseButton: true,
        });
        close();
      } catch (err) {
        fail(
          err,
          "jobResultsPage.itemRequest.sendFailed",
          "Could not send the item request.",
        );
      } finally {
        setSending(false);
      }
    },
    [jobId, preview, t, close, fail],
  );

  return {
    missingCount,
    opened,
    loading,
    sending,
    preview,
    open,
    close,
    send,
  };
};

/**
 * Turn a backend failure into something a reviewer can act on.
 *
 * The four `error_code`s this route raises mean genuinely different things —
 * one is a deployment gap, one is the reviewer's own address choice — and a
 * bare "request failed" would send them to the wrong person.
 */
const describeError = (err: unknown, fallback: string): string => {
  if (err instanceof ApiError) {
    switch (err.errorCode) {
      case "mail_not_configured":
        return "Sending is not configured for this environment yet — no verified sender address.";
      case "mail_recipient_not_allowed":
        return err.description ?? "That recipient is not allowed from here.";
      case "no_item_request_rows":
        return "No rows are waiting for a catalogue entry any more.";
      case "mail_send_failed":
        return `The mail provider rejected it: ${err.description ?? err.message}`;
      default:
        return err.message;
    }
  }
  return err instanceof Error ? err.message : fallback;
};
